import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CalendarDatePicker } from './CalendarDatePicker';
import '../App.css';

interface LLMUsageLogItem {
  id: string;
  user_id: string | null;
  user_type: string | null;
  trace_id: string | null;
  provider: string;
  model: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
  latency_ms: number | null;
  status: string | null;
  error_message: string | null;
  metadata: { agent_name?: string; workflow_name?: string; step_name?: string } | null;
  content_s3_key: string | null;
  ttft_ms: number | null;
  created_at: string;
}

interface Summary {
  total_calls: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost_usd: number;
  by_user_type: Record<string, number>;
  by_agent: Record<string, number>;
  by_provider: Record<string, number>;
}

interface FilterOptions {
  user_type: string[];
  provider: string[];
  status: string[];
  agent_name: string[];
  workflow_name: string[];
}

interface LLMUsageReconciliationItem {
  id: number;
  reconciliation_date: string;
  provider: string;
  our_request_count: number;
  our_input_tokens: number;
  our_output_tokens: number;
  our_total_tokens: number;
  our_cost_usd: number;
  provider_request_count: number | null;
  provider_input_tokens: number | null;
  provider_output_tokens: number | null;
  provider_total_tokens: number | null;
  provider_cost_usd: number | null;
  variance_input_tokens: number | null;
  variance_output_tokens: number | null;
  variance_cost_usd: number | null;
  status: string;
  error_message: string | null;
  raw_provider_response: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface LLMUsageProps {
  apiUrl: (path: string) => string;
  getAuthHeaders: (payload?: any, method?: string) => Record<string, string>;
  safeFetch?: (input: RequestInfo, init?: RequestInit) => Promise<Response>;
}

const defaultDateFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};
const defaultDateTo = () => new Date().toISOString().slice(0, 10);

const LLMUsage: React.FC<LLMUsageProps> = ({
  apiUrl,
  getAuthHeaders,
  safeFetch = fetch,
}) => {
  const apiUrlRef = useRef(apiUrl);
  const getAuthHeadersRef = useRef(getAuthHeaders);
  const safeFetchRef = useRef(safeFetch);
  apiUrlRef.current = apiUrl;
  getAuthHeadersRef.current = getAuthHeaders;
  safeFetchRef.current = safeFetch;

  const [summary, setSummary] = useState<Summary | null>(null);
  const [items, setItems] = useState<LLMUsageLogItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);

  const [dateFrom, setDateFrom] = useState(() => defaultDateFrom());
  const [dateTo, setDateTo] = useState(() => defaultDateTo());
  const [userType, setUserType] = useState('');
  const [userId, setUserId] = useState('');
  const [agentName, setAgentName] = useState('');
  const [workflowName, setWorkflowName] = useState('');
  const [stepName, setStepName] = useState('');
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [status, setStatus] = useState('');
  const [skip, setSkip] = useState(0);
  const limit = 50;
  const [recalculating, setRecalculating] = useState<string | 'all' | null>(null);

  // Sub-tab: 'usage' | 'reconciliation'
  const [llmSubTab, setLlmSubTab] = useState<'usage' | 'reconciliation'>('usage');

  // Reconciliation state
  const [reconItems, setReconItems] = useState<LLMUsageReconciliationItem[]>([]);
  const [reconTotal, setReconTotal] = useState(0);
  const [reconLoading, setReconLoading] = useState(false);
  const [reconError, setReconError] = useState<string | null>(null);
  const [reconDateFrom, setReconDateFrom] = useState(() => defaultDateFrom());
  const [reconDateTo, setReconDateTo] = useState(() => defaultDateTo());
  const [reconProvider, setReconProvider] = useState('');
  const [reconStatus, setReconStatus] = useState('');
  const [reconSkip, setReconSkip] = useState(0);
  const reconLimit = 50;
  const [reconRunning, setReconRunning] = useState(false);
  const [reconDeleting, setReconDeleting] = useState(false);
  const [runDateFrom, setRunDateFrom] = useState(() => defaultDateFrom());
  const [runDateTo, setRunDateTo] = useState(() => defaultDateTo());

  const fetchFilterOptions = useCallback(async () => {
    try {
      const res = await safeFetchRef.current(apiUrlRef.current('/api/v1/admin/llm-usage/filter-options'), {
        headers: getAuthHeadersRef.current(),
      });
      if (res.ok) {
        const data = await res.json();
        setFilterOptions(data);
      }
    } catch {
      // non-blocking
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const params: Record<string, string> = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (userType) params.user_type = userType;
      if (userId.trim()) params.user_id = userId.trim();
      if (agentName) params.agent_name = agentName;
      if (workflowName) params.workflow_name = workflowName;
      if (stepName.trim()) params.step_name = stepName.trim();
      if (provider) params.provider = provider;
      if (status) params.status = status;
      const q = new URLSearchParams(params).toString();
      const res = await safeFetchRef.current(apiUrlRef.current(`/api/v1/admin/llm-usage/summary?${q}`), {
        headers: getAuthHeadersRef.current(),
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch {
      setSummary(null);
    } finally {
      setLoadingSummary(false);
    }
  }, [dateFrom, dateTo, userType, userId, agentName, workflowName, stepName, provider, status]);

  const fetchItems = useCallback(async (skipOverride?: number) => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        date_from: dateFrom,
        date_to: dateTo,
        user_type: userType,
        user_id: userId.trim(),
        agent_name: agentName,
        workflow_name: workflowName,
        step_name: stepName.trim(),
        provider,
        model: model.trim(),
        status,
        skip: String(skipOverride !== undefined ? skipOverride : skip),
        limit: String(limit),
      };
      const q = new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== ''))).toString();
      const res = await safeFetchRef.current(apiUrlRef.current(`/api/v1/admin/llm-usage?${q}`), {
        headers: getAuthHeadersRef.current(),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      const data = await res.json();
      setItems(data.items || []);
      setTotal(data.total ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load usage');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, userType, userId, agentName, workflowName, stepName, provider, model, status, skip]);

  const recalculateCost = useCallback(async (ids: string[]) => {
    if (ids.length === 0) return;
    setRecalculating(ids.length === 1 ? ids[0] : 'all');
    setError(null);
    try {
      const res = await safeFetchRef.current(apiUrlRef.current('/api/v1/admin/llm-usage/recalculate-cost'), {
        method: 'POST',
        headers: getAuthHeadersRef.current({ ids }, 'POST'),
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) {
        const t = await res.json().catch(() => ({}));
        throw new Error(Array.isArray(t.detail) ? t.detail[0]?.msg : t.detail || `HTTP ${res.status}`);
      }
      await fetchItems();
      await fetchSummary();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recalculate failed');
    } finally {
      setRecalculating(null);
    }
  }, [fetchItems, fetchSummary]);

  const fetchReconList = useCallback(async (skipOverride?: number) => {
    setReconLoading(true);
    setReconError(null);
    try {
      const params: Record<string, string> = {
        date_from: reconDateFrom,
        date_to: reconDateTo,
        skip: String(skipOverride !== undefined ? skipOverride : reconSkip),
        limit: String(reconLimit),
      };
      if (reconProvider) params.provider = reconProvider;
      if (reconStatus) params.status = reconStatus;
      const q = new URLSearchParams(params).toString();
      const res = await safeFetchRef.current(apiUrlRef.current(`/api/v1/admin/llm-reconciliation?${q}`), {
        headers: getAuthHeadersRef.current(),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => `HTTP ${res.status}`));
      const data = await res.json();
      setReconItems(data.items || []);
      setReconTotal(data.total ?? 0);
    } catch (e) {
      setReconError(e instanceof Error ? e.message : 'Failed to load reconciliation');
      setReconItems([]);
      setReconTotal(0);
    } finally {
      setReconLoading(false);
    }
  }, [reconDateFrom, reconDateTo, reconProvider, reconStatus, reconSkip]);

  const runReconciliation = useCallback(async () => {
    setReconRunning(true);
    setReconError(null);
    try {
      const body: { date_from: string; date_to?: string; providers?: string[] } = {
        date_from: runDateFrom,
        date_to: runDateTo || runDateFrom,
      };
      const res = await safeFetchRef.current(apiUrlRef.current('/api/v1/admin/llm-reconciliation/run'), {
        method: 'POST',
        headers: getAuthHeadersRef.current(body, 'POST'),
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const t = await res.json().catch(() => ({}));
        throw new Error(Array.isArray(t.detail) ? t.detail[0]?.msg : t.detail || `HTTP ${res.status}`);
      }
      await fetchReconList(0);
      setReconSkip(0);
    } catch (e) {
      setReconError(e instanceof Error ? e.message : 'Run reconciliation failed');
    } finally {
      setReconRunning(false);
    }
  }, [runDateFrom, runDateTo, fetchReconList]);

  const deleteReconRecords = useCallback(async () => {
    const fromStr = runDateFrom;
    const toStr = runDateTo || runDateFrom;
    if (!window.confirm(`Delete all reconciliation records from ${fromStr} to ${toStr}? You can re-run reconciliation after.`)) return;
    setReconDeleting(true);
    setReconError(null);
    try {
      const params = new URLSearchParams({ date_from: fromStr });
      if (toStr !== fromStr) params.set('date_to', toStr);
      const res = await safeFetchRef.current(apiUrlRef.current(`/api/v1/admin/llm-reconciliation?${params.toString()}`), {
        method: 'DELETE',
        headers: getAuthHeadersRef.current(),
      });
      if (!res.ok) {
        const t = await res.json().catch(() => ({}));
        throw new Error(t.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      await fetchReconList(0);
      setReconSkip(0);
      setReconError(null);
      if (data.deleted_count !== undefined) {
        setReconError(null);
        window.alert(`Deleted ${data.deleted_count} record(s).`);
      }
    } catch (e) {
      setReconError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setReconDeleting(false);
    }
  }, [runDateFrom, runDateTo, fetchReconList]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    if (llmSubTab === 'reconciliation') {
      fetchReconList();
    }
  }, [llmSubTab, fetchReconList]);

  // Reset to first page when any filter changes so the list matches the summary
  useEffect(() => {
    setSkip(0);
  }, [dateFrom, dateTo, userType, userId, agentName, workflowName, stepName, provider, model, status]);

  useEffect(() => {
    fetchSummary();
    fetchItems();
  }, [fetchSummary, fetchItems]);

  const handleApply = () => {
    setSkip(0);
    fetchSummary();
    fetchItems(0);
  };

  const pageCount = Math.ceil(total / limit) || 1;
  const currentPage = Math.floor(skip / limit) + 1;

  const reconPageCount = Math.ceil(reconTotal / reconLimit) || 1;
  const reconCurrentPage = Math.floor(reconSkip / reconLimit) + 1;

  return (
    <div className="llm-usage-page" style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 8 }}>LLM Usage</h1>
      <p style={{ color: '#666', marginBottom: 16, fontSize: 14 }}>
        View usage across users, agents, and workflows. Reconcile with provider usage/cost daily.
      </p>

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #e6e8ea' }}>
        <button
          type="button"
          onClick={() => setLlmSubTab('usage')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderBottom: llmSubTab === 'usage' ? '2px solid #1976d2' : '2px solid transparent',
            background: 'none',
            cursor: 'pointer',
            fontWeight: llmSubTab === 'usage' ? 600 : 400,
            color: llmSubTab === 'usage' ? '#1976d2' : '#666',
          }}
        >
          Usage
        </button>
        <button
          type="button"
          onClick={() => setLlmSubTab('reconciliation')}
          style={{
            padding: '10px 20px',
            border: 'none',
            borderBottom: llmSubTab === 'reconciliation' ? '2px solid #1976d2' : '2px solid transparent',
            background: 'none',
            cursor: 'pointer',
            fontWeight: llmSubTab === 'reconciliation' ? 600 : 400,
            color: llmSubTab === 'reconciliation' ? '#1976d2' : '#666',
          }}
        >
          Reconciliation
        </button>
      </div>

      {llmSubTab === 'reconciliation' ? (
        /* Reconciliation tab */
        <>
          <p style={{ color: '#666', marginBottom: 24, fontSize: 14 }}>
            Daily reconciliation of our usage logs vs provider-reported usage/cost (Anthropic, OpenAI, AWS Bedrock).
          </p>

          {/* Run reconciliation */}
          <div style={{ marginBottom: 24, padding: 16, background: '#f0f7ff', borderRadius: 8, border: '1px solid #bbdefb' }}>
            <h2 style={{ fontSize: 16, marginBottom: 12 }}>Run reconciliation</h2>
            <div className="llm-filter-row" style={{ marginBottom: 12 }}>
              <div className="llm-filter-group">
                <CalendarDatePicker
                  label="From date"
                  value={runDateFrom}
                  onChange={setRunDateFrom}
                  dateDisplayFormat="short"
                  placeholder="Select date"
                />
              </div>
              <div className="llm-filter-group">
                <CalendarDatePicker
                  label="To date"
                  value={runDateTo}
                  onChange={setRunDateTo}
                  dateDisplayFormat="short"
                  placeholder="Select date"
                />
              </div>
              <button
                type="button"
                className="llm-btn-run"
                disabled={reconRunning}
                onClick={runReconciliation}
              >
                {reconRunning ? 'Running…' : 'Run reconciliation'}
              </button>
              <button
                type="button"
                className="llm-btn-apply"
                disabled={reconDeleting || reconRunning}
                onClick={deleteReconRecords}
                style={{ marginLeft: 8 }}
                title="Delete reconciliation records in the From–To range so you can re-run"
              >
                {reconDeleting ? 'Deleting…' : 'Delete records in range'}
              </button>
            </div>
            <span style={{ fontSize: 12, color: '#666' }}>Fetches provider usage/cost and compares to our logs. One row per (date, provider). Use “Delete records in range” to clear rows and re-run.</span>
          </div>

          {/* Reconciliation filters */}
          <div className="llm-filter-row">
            <div className="llm-filter-group">
              <CalendarDatePicker
                label="From"
                value={reconDateFrom}
                onChange={setReconDateFrom}
                dateDisplayFormat="short"
                placeholder="Select date"
              />
            </div>
            <div className="llm-filter-group">
              <CalendarDatePicker
                label="To"
                value={reconDateTo}
                onChange={setReconDateTo}
                dateDisplayFormat="short"
                placeholder="Select date"
              />
            </div>
            <label className="llm-filter-group">
              <span className="llm-form-label">Provider</span>
              <select
                className="llm-select"
                value={reconProvider}
                onChange={(e) => setReconProvider(e.target.value)}
                style={{ minWidth: 130 }}
              >
                <option value="">All</option>
                <option value="anthropic">anthropic</option>
                <option value="openai">openai</option>
                <option value="bedrock">bedrock</option>
              </select>
            </label>
            <label className="llm-filter-group">
              <span className="llm-form-label">Status</span>
              <select
                className="llm-select"
                value={reconStatus}
                onChange={(e) => setReconStatus(e.target.value)}
                style={{ minWidth: 160 }}
              >
                <option value="">All</option>
                <option value="reconciled">reconciled</option>
                <option value="variance">variance</option>
                <option value="provider_unavailable">provider_unavailable</option>
                <option value="error">error</option>
              </select>
            </label>
            <button
              type="button"
              className="llm-btn-apply"
              onClick={() => { setReconSkip(0); fetchReconList(0); }}
            >
              Apply
            </button>
          </div>

          {reconError && <p style={{ color: '#c62828', marginBottom: 12 }}>{reconError}</p>}

          <div style={{ overflowX: 'auto', border: '1px solid #e6e8ea', borderRadius: 8 }}>
            {reconLoading ? (
              <p style={{ padding: 24 }}>Loading…</p>
            ) : (
              <>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1000 }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e6e8ea' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Date</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Provider</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Our requests</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Our tokens</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Our cost</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Provider tokens</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Provider cost</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Variance cost</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Error</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Raw response</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reconItems.length === 0 ? (
                      <tr>
                        <td colSpan={11} style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                          No reconciliation records. Run reconciliation for a date range above.
                        </td>
                      </tr>
                    ) : (
                      reconItems.map((row) => (
                        <tr key={`${row.reconciliation_date}-${row.provider}`} style={{ borderBottom: '1px solid #e6e8ea' }}>
                          <td style={{ padding: '10px 12px', fontSize: 13 }}>{row.reconciliation_date}</td>
                          <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 13 }}>{row.provider}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{row.our_request_count.toLocaleString()}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>{(row.our_total_tokens ?? 0).toLocaleString()}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>${Number(row.our_cost_usd).toFixed(4)}</td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            {row.provider_total_tokens != null ? row.provider_total_tokens.toLocaleString() : '—'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            {row.provider_cost_usd != null ? `$${Number(row.provider_cost_usd).toFixed(4)}` : '—'}
                          </td>
                          <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                            {row.variance_cost_usd != null ? (
                              <span style={{ color: Number(row.variance_cost_usd) !== 0 ? '#c62828' : undefined }}>
                                ${Number(row.variance_cost_usd).toFixed(4)}
                              </span>
                            ) : '—'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            <span style={{
                              color: row.status === 'reconciled' ? '#2e7d32' : row.status === 'variance' ? '#ed6c02' : row.status === 'error' ? '#c62828' : '#666',
                              fontWeight: 500,
                            }}>
                              {row.status}
                            </span>
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: 12, color: '#666', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.error_message || '—'}
                          </td>
                          <td style={{ padding: '10px 12px', fontSize: 12, verticalAlign: 'top' }}>
                            {row.raw_provider_response != null ? (
                              <details>
                                <summary style={{ cursor: 'pointer', color: '#1976d2' }}>View full response</summary>
                                <pre style={{ margin: '8px 0 0', padding: 8, background: '#f5f5f5', borderRadius: 4, fontSize: 11, overflow: 'auto', maxHeight: 300, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                  {JSON.stringify(row.raw_provider_response, null, 2)}
                                </pre>
                              </details>
                            ) : '—'}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {reconTotal > reconLimit && (
                  <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e6e8ea' }}>
                    <span style={{ fontSize: 13 }}>
                      {reconSkip + 1}–{Math.min(reconSkip + reconLimit, reconTotal)} of {reconTotal}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        disabled={reconSkip === 0}
                        onClick={() => { setReconSkip((s) => Math.max(0, s - reconLimit)); fetchReconList(Math.max(0, reconSkip - reconLimit)); }}
                        style={{ padding: '6px 12px', cursor: reconSkip === 0 ? 'not-allowed' : 'pointer' }}
                      >
                        Previous
                      </button>
                      <span style={{ alignSelf: 'center' }}>Page {reconCurrentPage} of {reconPageCount}</span>
                      <button
                        type="button"
                        disabled={reconSkip + reconLimit >= reconTotal}
                        onClick={() => { setReconSkip((s) => s + reconLimit); fetchReconList(reconSkip + reconLimit); }}
                        style={{ padding: '6px 12px', cursor: reconSkip + reconLimit >= reconTotal ? 'not-allowed' : 'pointer' }}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      ) : (
        /* Usage tab */
        <>
      {/* Filters */}
      <div className="llm-filter-row">
        <div className="llm-filter-group">
          <CalendarDatePicker
            label="From"
            value={dateFrom}
            onChange={setDateFrom}
            dateDisplayFormat="short"
            placeholder="Select date"
          />
        </div>
        <div className="llm-filter-group">
          <CalendarDatePicker
            label="To"
            value={dateTo}
            onChange={setDateTo}
            dateDisplayFormat="short"
            placeholder="Select date"
          />
        </div>
        <label className="llm-filter-group">
          <span className="llm-form-label">User type</span>
          <select
            className="llm-select"
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
            style={{ minWidth: 120 }}
          >
            <option value="">All</option>
            {(filterOptions?.user_type || []).map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
        <label className="llm-filter-group">
          <span className="llm-form-label">User ID</span>
          <input
            type="text"
            className="llm-input-text"
            placeholder="User ID"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            style={{ width: 120 }}
          />
        </label>
        <label className="llm-filter-group">
          <span className="llm-form-label">Agent</span>
          <select
            className="llm-select"
            value={agentName}
            onChange={(e) => setAgentName(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <option value="">All</option>
            {(filterOptions?.agent_name || []).map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
        <label className="llm-filter-group">
          <span className="llm-form-label">Workflow</span>
          <select
            className="llm-select"
            value={workflowName}
            onChange={(e) => setWorkflowName(e.target.value)}
            style={{ minWidth: 140 }}
          >
            <option value="">All</option>
            {(filterOptions?.workflow_name || []).map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        </label>
        <label className="llm-filter-group">
          <span className="llm-form-label">Step name</span>
          <input
            type="text"
            className="llm-input-text"
            placeholder="Step name"
            value={stepName}
            onChange={(e) => setStepName(e.target.value)}
            style={{ width: 180 }}
          />
        </label>
        <label className="llm-filter-group">
          <span className="llm-form-label">Provider</span>
          <select
            className="llm-select"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            style={{ minWidth: 120 }}
          >
            <option value="">All</option>
            {(filterOptions?.provider || []).map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </label>
        <label className="llm-filter-group">
          <span className="llm-form-label">Model</span>
          <input
            type="text"
            className="llm-input-text"
            placeholder="Partial match"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            style={{ width: 140 }}
          />
        </label>
        <label className="llm-filter-group">
          <span className="llm-form-label">Status</span>
          <select
            className="llm-select"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            style={{ minWidth: 100 }}
          >
            <option value="">All</option>
            {(filterOptions?.status || []).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className="llm-btn-apply"
          onClick={handleApply}
        >
          Apply
        </button>
      </div>

      {error && <p style={{ color: '#c62828', marginBottom: 12 }}>{error}</p>}

      {/* Summary */}
      <div style={{ marginBottom: 24, padding: 16, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e6e8ea' }}>
        <h2 style={{ fontSize: 16, marginBottom: 12 }}>Summary</h2>
        {loadingSummary ? (
          <p>Loading…</p>
        ) : summary ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
            <div><strong>Calls:</strong> {summary.total_calls.toLocaleString()}</div>
            <div><strong>Tokens in:</strong> {summary.total_input_tokens.toLocaleString()}</div>
            <div><strong>Tokens out:</strong> {summary.total_output_tokens.toLocaleString()}</div>
            <div><strong>Total tokens:</strong> {summary.total_tokens.toLocaleString()}</div>
            <div><strong>Cost (USD):</strong> ${Number(summary.total_cost_usd).toFixed(4)}</div>
            {Object.keys(summary.by_user_type).length > 0 && (
              <div>
                <strong>By user type:</strong>{' '}
                {Object.entries(summary.by_user_type).map(([k, v]) => `${k}: ${v}`).join(', ')}
              </div>
            )}
            {Object.keys(summary.by_agent).length > 0 && (
              <div>
                <strong>By agent:</strong>{' '}
                {Object.entries(summary.by_agent).slice(0, 5).map(([k, v]) => `${k}: ${v}`).join(', ')}
                {Object.keys(summary.by_agent).length > 5 ? ' …' : ''}
              </div>
            )}
            {Object.keys(summary.by_provider).length > 0 && (
              <div>
                <strong>By provider:</strong>{' '}
                {Object.entries(summary.by_provider).map(([k, v]) => `${k}: ${v}`).join(', ')}
              </div>
            )}
          </div>
        ) : (
          <p style={{ color: '#666' }}>No data for the selected filters.</p>
        )}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', border: '1px solid #e6e8ea', borderRadius: 8 }}>
        {loading ? (
          <p style={{ padding: 24 }}>Loading…</p>
        ) : (
          <>
            {items.length > 0 && (
              <div style={{ padding: '8px 12px', borderBottom: '1px solid #e6e8ea', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={!!recalculating}
                  onClick={() => recalculateCost(items.map((r) => r.id))}
                  style={{
                    padding: '6px 12px',
                    cursor: recalculating === 'all' ? 'not-allowed' : 'pointer',
                    background: '#2e7d32',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    fontSize: 13,
                  }}
                >
                  {recalculating === 'all' ? 'Recalculating…' : 'Recalculate cost for all on this page'}
                </button>
                <span style={{ fontSize: 12, color: '#666' }}>Uses current LLM Pricing table.</span>
              </div>
            )}
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e6e8ea' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Time</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>User</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Agent / Workflow / Step</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Provider / Model</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Tokens in</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Tokens out</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 600 }}>Cost (USD)</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#666' }}>
                      No usage logs for the selected filters.
                    </td>
                  </tr>
                ) : (
                  items.map((row) => (
                    <tr key={row.id} style={{ borderBottom: '1px solid #e6e8ea' }}>
                      <td style={{ padding: '10px 12px', fontSize: 13 }}>
                        {new Date(row.created_at).toLocaleString()}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 13 }}>
                        {row.user_type || '—'} {row.user_id ? `(${row.user_id})` : ''}
                      </td>
                      <td style={{ padding: '10px 12px', fontSize: 13 }}>
                        {row.metadata?.agent_name || '—'} / {row.metadata?.workflow_name || '—'} / {row.metadata?.step_name || '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                        {row.provider} / {row.model}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{(row.input_tokens ?? 0).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>{(row.output_tokens ?? 0).toLocaleString()}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>${Number(row.cost_usd).toFixed(4)}</td>
                      <td style={{ padding: '10px 12px' }}>{row.status || '—'}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <button
                          type="button"
                          disabled={!!recalculating}
                          onClick={() => recalculateCost([row.id])}
                          style={{
                            padding: '4px 10px',
                            cursor: recalculating === row.id ? 'not-allowed' : 'pointer',
                            background: '#1976d2',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        >
                          {recalculating === row.id ? '…' : 'Recalculate'}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {total > limit && (
              <div style={{ padding: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #e6e8ea' }}>
                <span style={{ fontSize: 13 }}>
                  {skip + 1}–{Math.min(skip + limit, total)} of {total}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    disabled={skip === 0}
                    onClick={() => { setSkip((s) => Math.max(0, s - limit)); }}
                    style={{ padding: '6px 12px', cursor: skip === 0 ? 'not-allowed' : 'pointer' }}
                  >
                    Previous
                  </button>
                  <span style={{ alignSelf: 'center' }}>Page {currentPage} of {pageCount}</span>
                  <button
                    type="button"
                    disabled={skip + limit >= total}
                    onClick={() => setSkip((s) => s + limit)}
                    style={{ padding: '6px 12px', cursor: skip + limit >= total ? 'not-allowed' : 'pointer' }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default LLMUsage;
